import React, { useCallback, useEffect, useMemo, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";

import {
    Button,
    CESION_ECHEQ_ACTIVITY,
    EcheqEndorseTicket,
    echeqEndorseTicketTestIds,
    NEGOTIATE_ECHEQ_ACTIVITY,
    resolveEcheqEndorseTitleKey,
} from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";
import CesionEcheqTransactionData from "pages/checks/Echeqs/EcheqTransactions/CesionEcheqTransactionData";
import EcheqTransactionData from "pages/checks/Echeqs/EcheqTransactions/EcheqTransactionData";
import NegotiateEcheqTransactionData from "pages/checks/Echeqs/EcheqTransactions/NegotiateEcheqTransactionData";

import { selectors as checksSelectors } from "reducers/checks";
import { actions as formActions } from "reducers/form";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";
import { actions as transactionsActions, selectors as transactionsSelectors } from "reducers/transactions";

import * as config from "util/config";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import {
    canUserSignPendingFromPotentialSchemes,
    exportGenericTicketPdfWithSuccessMessage,
    isMvp3PendingTransactionsEnabled,
    isTransactionPendingProcessing,
} from "util/transaction";
import {
    isUxDashboardImprovementsEnabled,
    resolveEmbeddedV3GoBackPath,
    useEmbeddedV3ShellLayoutSync,
} from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";

import buildEcheqEndorseDataInput, {
    buildEcheqEndorseCancelledBy,
    formatEcheqEndorseSignatureDate,
} from "./endorseTicketData";
import buildEcheqTicketPdfData from "./echeqTicketPdfData";

const ECHEQ_TICKET_SCOPES = ["echeqs", "echeqsPending"];
const ECHEQ_TICKET_GO_BACK_PATH = "/echeqs";

/**
 * V3 receipt for an ECHEQ endorsement, negotiation or assignment (cesión). Replaces the legacy
 * `EndorseTransaction` / `CesionEcheqTransaction` screens on pending and final-state routes:
 * the status banner, detail rows, signatures panel, signature schemes and final PDF download now
 * come from the V3 flow instead of the legacy ticket.
 */
function EndorseTicketV3Page({
    dispatch,
    transactionData,
    isDesktop,
    loggedUser,
    fullName,
    potentialSignaturesData,
    administrationScheme,
    lastHrefPathname,
}) {
    const [signaturesDrawerOpenMobile, setSignaturesDrawerOpenMobile] = useState(false);
    const [exportPdfLoading, setExportPdfLoading] = useState(false);

    const status = transactionData?.idTransactionStatus;
    const isPending = status === "PENDING" || status === "PENDING_TOKEN";
    const isFinished = status === "FINISHED";
    const idTransaction = transactionData?.idTransaction;
    const isNegotiate = transactionData?.idActivity === NEGOTIATE_ECHEQ_ACTIVITY;
    const isCesion = transactionData?.idActivity === CESION_ECHEQ_ACTIVITY;

    useEffect(() => {
        if (idTransaction) {
            dispatch(transactionsActions.getPotentialSignaturesData(idTransaction));
            return;
        }
        const flow = localStorage.getItem("flow");
        if (flow) {
            dispatch(push(JSON.parse(flow)));
        }
    }, [dispatch, idTransaction]);

    const getLabels = useCallback((id) => i18n.get(id), []);

    const ticketData = useMemo(() => buildEcheqEndorseDataInput(transactionData), [transactionData]);

    /** Same label the UI-kit prints on the receipt banner; reused for the header and the sign flow. */
    const title = useMemo(
        () => (transactionData?.idActivity ? i18n.get(resolveEcheqEndorseTitleKey(transactionData.idActivity)) : ""),
        [transactionData],
    );

    /** Mobile signature control banner (legacy `TransactionTicket` hint). */
    const titleHintText = useMemo(() => {
        if (!isPending || !transactionData?.control) {
            return undefined;
        }
        return config.get("frontend.mobileSignatureControl.text", "") || undefined;
    }, [isPending, transactionData]);

    const goBackPath = useMemo(() => resolveEmbeddedV3GoBackPath(ECHEQ_TICKET_GO_BACK_PATH, lastHrefPathname), [
        lastHrefPathname,
    ]);

    const handleGoBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    /** Downloads the final receipt using the same generic PDF service as the legacy ticket. */
    const exportPdf = useCallback(async () => {
        if (!transactionData) {
            return;
        }
        setExportPdfLoading(true);
        try {
            await exportGenericTicketPdfWithSuccessMessage(
                buildEcheqTicketPdfData(transactionData),
                dispatch,
                i18n.get("global.download.success"),
            );
        } finally {
            setExportPdfLoading(false);
        }
    }, [dispatch, transactionData]);

    const rightContentMobile = useMemo(() => {
        if (!isPending && !isFinished) {
            return null;
        }
        return (
            <Button
                data-testid={isPending ? echeqEndorseTicketTestIds.schemes : echeqEndorseTicketTestIds.download}
                aria-label={i18n.get(isPending ? "pendingTransaction.info.scheme" : "global.download.ticket")}
                variant="text"
                color="white"
                icon={isPending ? "Eye" : "SendDown"}
                iconVariant="outline"
                loading={isFinished && exportPdfLoading}
                onClick={isPending ? () => setSignaturesDrawerOpenMobile(true) : exportPdf}
            />
        );
    }, [exportPdf, exportPdfLoading, isFinished, isPending]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell: true,
        isDesktop,
        titleKey: "",
        headerTitle: title,
        goBackPath,
        scopes: ECHEQ_TICKET_SCOPES,
        rightContentMobile,
    });

    const preparedBy = useMemo(
        () => ({
            userFullName: transactionData?.creatorFullName || fullName,
            creationDateTime: isPending ? undefined : transactionData?.creationDate ?? undefined,
        }),
        [transactionData, fullName, isPending],
    );

    const signedBy = useMemo(
        () =>
            (transactionData?.signatures ?? []).map((signature) => ({
                userFirstName: signature.userFirstName,
                userLastName: signature.userLastName,
                userFullName: signature.userFullName,
                creationDateTime:
                    signature.creationDateTime ??
                    signature.modificationDateTime ??
                    signature.submitDateTime ??
                    signature.creationDate,
            })),
        [transactionData],
    );

    /** "Cancelado por": manual cancellation or the expiry daemon, as the legacy ticket resolved it. */
    const cancelledBy = useMemo(() => buildEcheqEndorseCancelledBy(transactionData), [transactionData]);

    /** Detail block reused as signature/cancellation preview content, as the legacy screen did. */
    const summaryDataComponent = useCallback(() => {
        if (isNegotiate) {
            return <NegotiateEcheqTransactionData isDesktop={isDesktop} />;
        }
        if (isCesion) {
            return <CesionEcheqTransactionData isDesktop={isDesktop} />;
        }
        return <EcheqTransactionData isDesktop={isDesktop} />;
    }, [isNegotiate, isCesion, isDesktop]);

    const signTransaction = useCallback(() => {
        dispatch(push("/transactions/sign"));
        dispatch(
            formActions.signTransactionData({
                idTransaction,
                idActivity: transactionData?.idActivity,
                idForm: "",
                content: summaryDataComponent(),
                title: resolveEcheqEndorseTitleKey(transactionData?.idActivity),
                pendingDispatch: transactionData?.pendingDispatch,
            }),
        );
        dispatch(
            formActions.signTransactionPreview({
                idActivity: transactionData?.idActivity,
                idTransaction,
                idForm: "",
                ticketData: transactionData,
            }),
        );
    }, [dispatch, idTransaction, transactionData, summaryDataComponent]);

    const cancelPendingTransaction = useCallback(() => {
        dispatch(push("/transactions/cancel"));
        dispatch(
            formActions.cancelTransactionData({
                idTransaction,
                content: summaryDataComponent(),
                title: resolveEcheqEndorseTitleKey(transactionData?.idActivity),
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity: transactionData?.idActivity, idForm: "" }));
    }, [dispatch, idTransaction, transactionData, summaryDataComponent]);

    const goToPendingAuthorizations = useCallback(() => {
        dispatch(push("/pendingTransaction/list"));
        if (!isMvp3PendingTransactionsEnabled()) {
            dispatch(transactionsActions.initPendings());
            dispatch(
                transactionsActions.loadListRequest(
                    {
                        pageNumber: 1,
                        status: "PENDING",
                        filterPendingDispatch: false,
                        functionRequest: "pendingTransactions",
                        idActivity: transactionData?.idActivity,
                    },
                    true,
                    false,
                ),
            );
        }
    }, [dispatch, transactionData]);

    const goToHistoricTransactions = useCallback(() => {
        dispatch(transactionsActions.setFilters(null));
        dispatch(push("/transactions/list"));
    }, [dispatch]);

    const isPendingProcessing = useMemo(
        () => (transactionData ? isTransactionPendingProcessing(transactionData) : false),
        [transactionData],
    );

    const showSignButton = useMemo(() => {
        if (!isPending || isPendingProcessing || transactionData?.control) {
            return false;
        }
        return canUserSignPendingFromPotentialSchemes(transactionData, loggedUser, potentialSignaturesData);
    }, [isPending, isPendingProcessing, transactionData, loggedUser, potentialSignaturesData]);

    const navigationButtons = useMemo(() => {
        if (!isFinished) {
            return [];
        }
        return [
            {
                "data-testid": echeqEndorseTicketTestIds.download,
                onClick: exportPdf,
                icon: "SendDown",
                label: getLabels("global.download"),
                loading: exportPdfLoading,
            },
        ];
    }, [exportPdf, exportPdfLoading, getLabels, isFinished]);

    const footerNavigationButtons = useMemo(() => {
        if (isPending) {
            const buttons = [
                {
                    "data-testid": echeqEndorseTicketTestIds.gotoPendings,
                    onClick: goToPendingAuthorizations,
                    icon: "WritingPending",
                    label: getLabels("goto.pendings.transaction.autorizaciones"),
                    variant: "outline",
                },
            ];
            if (!isPendingProcessing && !transactionData?.control) {
                buttons.push({
                    "data-testid": echeqEndorseTicketTestIds.delete,
                    onClick: cancelPendingTransaction,
                    icon: "Trash",
                    label: getLabels("transaction.delete"),
                    variant: "outline",
                });
            }
            if (showSignButton) {
                buttons.push({
                    "data-testid": echeqEndorseTicketTestIds.sign,
                    onClick: signTransaction,
                    icon: "AddServices",
                    label: getLabels("forms.signTransaction.link"),
                    variant: "solid",
                });
            }
            return buttons;
        }

        return [
            {
                "data-testid": echeqEndorseTicketTestIds.gotoHistoric,
                onClick: goToHistoricTransactions,
                icon: "Refund",
                label: getLabels("goto.historic.transaction"),
                variant: "outline",
            },
            {
                "data-testid": echeqEndorseTicketTestIds.gotoHome,
                onClick: () => dispatch(push(getPostLoginHomePath(isUxDashboardImprovementsEnabled()))),
                icon: "Home",
                label: getLabels("global.goToHome"),
                variant: "solid",
            },
        ];
    }, [
        dispatch,
        getLabels,
        isPending,
        isPendingProcessing,
        transactionData,
        goToPendingAuthorizations,
        goToHistoricTransactions,
        cancelPendingTransaction,
        showSignButton,
        signTransaction,
    ]);

    if (!ticketData) {
        return null;
    }

    return (
        <EcheqEndorseTicket
            goBack={handleGoBack}
            getLabels={getLabels}
            data={ticketData}
            title={title}
            titleHintText={titleHintText}
            formatOperationDate={formatLegacyWebDateTimeString}
            transactionTicketProps={{ formatSignatureDate: formatEcheqEndorseSignatureDate }}
            preparedBy={preparedBy}
            signedBy={signedBy}
            cancelledBy={cancelledBy}
            navigationButtons={navigationButtons}
            footerNavigationButtons={footerNavigationButtons}
            potentialSignaturesData={potentialSignaturesData ?? undefined}
            signatureSchemeDisplayMode={administrationScheme === "medium" ? "medium" : "advanced"}
            openSignatureSchemeMobile={signaturesDrawerOpenMobile}
        />
    );
}

EndorseTicketV3Page.propTypes = {
    dispatch: func.isRequired,
    transactionData: shape({}),
    isDesktop: bool.isRequired,
    loggedUser: shape({}),
    fullName: string,
    potentialSignaturesData: arrayOf(shape({})),
    administrationScheme: string,
    lastHrefPathname: string,
};

EndorseTicketV3Page.defaultProps = {
    transactionData: null,
    loggedUser: null,
    fullName: "",
    potentialSignaturesData: null,
    administrationScheme: "advanced",
    lastHrefPathname: "",
};

const mapStateToProps = (state) => ({
    transactionData: checksSelectors.getTransactionEcheqData(state),
    loggedUser: sessionSelectors.getUser(state),
    fullName: sessionSelectors.getUser(state)?.userFullName,
    potentialSignaturesData: transactionsSelectors.getPotentialSignaturesData(state),
    administrationScheme: sessionSelectors.getAdministrationScheme(state),
    lastHrefPathname: statusSelectors.getPathname(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EndorseTicketV3Page);
